import {AcVariantCoverage, AcVariantsCoverage, BeesafeAcCoverageVariant} from './ac-variants.coverage';
import {
  SelectedOfferService
} from '../../../../core/services/impl/section-services/selected-offer/selected-offer.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {OfferCoverageCode} from '../../../../core/consts/offer-coverages/offer-coverage-code.enum';
import {InsuranceCompanyCode} from '../../../models/insurance-company-code.enum';
import {AcVariantsModificationOption} from '../../../../core/consts/offer-modification-code.enum';
import {
  OfferModificationsService
} from '../../../../core/services/impl/offer-modifications/offer-modifications.service';

export class AcVariantsCoveragesComparisonConfig {

  private readonly translationsContainerKey = 'AC_VARIANTS_COMPARISON';

  constructor(public icCode: InsuranceCompanyCode,
              public offerModificationsService: OfferModificationsService,
              public selectedOfferService?: SelectedOfferService) {
  }

  public getVariantsOrder(): AcVariantCoverage[] {
    switch (this.icCode) {
      case InsuranceCompanyCode.BEESAFE:
        return [
          BeesafeAcCoverageVariant.MINI,
          BeesafeAcCoverageVariant.WORKSHOP,
          BeesafeAcCoverageVariant.ASO,
        ];
      case InsuranceCompanyCode.PEVNO:
        return [
          AcVariantsModificationOption.SERWIS_ASO,
          AcVariantsModificationOption.WARSZTAT_PLUS,
          AcVariantsModificationOption.WARSZTAT,
          AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
          AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
        ];
      case InsuranceCompanyCode.UNIQA:
        return [
          AcVariantsModificationOption.SERWIS_ASO,
          AcVariantsModificationOption.WARSZTAT_PLUS,
          AcVariantsModificationOption.WARSZTAT,
          AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
          AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
        ];
      default:
        console.warn(`Unsupported ic code ${this.icCode} for generating ac coverages order.`);
    }
  }

  public getShortComparisonConfig(): AcVariantsCoveragesCoverageConfigModel[] {
    return [
      {
        coverageName: AcVariantsCoverage.AC_VEHICLE_THEFT_AND_ELEMENTAL,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.NONE,
            value: false
          },
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: true
          }
        ],
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_VEHICLE_THEFT_AND_ELEMENTAL}.tooltip`
      },
      {
        coverageName: AcVariantsCoverage.AC_HAPPENING_ACCIDENT_AND_DAMAGE,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.NONE,
            value: false
          },
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_HAPPENING_ACCIDENT_AND_DAMAGE}.ANIMAL_COLLISIONS`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: true
          }
        ],
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_HAPPENING_ACCIDENT_AND_DAMAGE}.tooltip`
      },
      {
        coverageName: AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.NONE,
            value: false
          },
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE}.estimate`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE}.partnerWorkshop`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE}.aso`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_PARTS,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.NONE,
            value: false
          },
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_PARTS}.replacements`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_PARTS}.replacements`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_PARTS}.original`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_EXCESS,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.NONE,
            value: false
          },
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_EXCESS}.500`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_EXCESS}.500`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_EXCESS}.500`
          }
        ]
      }
    ]
  }

  public getFullComparisonConfig(): AcVariantsCoveragesCoverageConfigModel[] {
    switch (this.icCode) {
      case InsuranceCompanyCode.BEESAFE:
        return this.getBeesafeFullComparisonConfig();
      case InsuranceCompanyCode.PEVNO:
        return this.getUniqaPevnoFullComparisonConfig();
      case InsuranceCompanyCode.UNIQA:
        return this.getUniqaPevnoFullComparisonConfig();
      default:
        console.warn(`Unsupported ic code ${this.icCode} for generating ac coverages comparison.`);
    }
  }

  private getUniqaPevnoFullComparisonConfig(): AcVariantsCoveragesCoverageConfigModel[] {
    const vehicleValue = this.offerModificationsService.getVehicleValueFormatted();
    const isPevno = this.icCode === InsuranceCompanyCode.PEVNO;
    return [
      {
        coverageName: AcVariantsCoverage.AC_PRODUCT_NAME,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: `${this.translationsContainerKey}.AC_PRODUCT_NAME.aso`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: `${this.translationsContainerKey}.AC_PRODUCT_NAME.servicePlus`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: `${this.translationsContainerKey}.AC_PRODUCT_NAME.service`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: `${this.translationsContainerKey}.AC_PRODUCT_NAME.estimate`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: `${this.translationsContainerKey}.AC_PRODUCT_NAME.estimateDeprecation`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE,
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE}.tooltip`,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: `${this.translationsContainerKey}.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE.cashlessSettlement`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: `${this.translationsContainerKey}.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE.cashlessSettlement`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: `${this.translationsContainerKey}.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE.cashlessSettlement`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: `${this.translationsContainerKey}.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE.estimate`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: `${this.translationsContainerKey}.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE.estimate`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_REPAIR_PLACE,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: `${this.translationsContainerKey}.AC_REPAIR_PLACE.aso`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: `${this.translationsContainerKey}.AC_REPAIR_PLACE.unauthorizedService`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: `${this.translationsContainerKey}.AC_REPAIR_PLACE.unauthorizedService`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: false
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: false
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_PARTS,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: `${this.translationsContainerKey}.AC_PARTS.original`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: `${this.translationsContainerKey}.AC_PARTS.original`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: `${this.translationsContainerKey}.AC_PARTS.replacements`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: `${this.translationsContainerKey}.AC_PARTS.replacements`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: `${this.translationsContainerKey}.AC_PARTS.replacements`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_DEPRECATION,
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_DEPRECATION}.tooltip`,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: `${this.translationsContainerKey}.AC_DEPRECATION.AC_DEPRECATION_ABOLISHED`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: `${this.translationsContainerKey}.AC_DEPRECATION.AC_DEPRECATION_ABOLISHED`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: `${this.translationsContainerKey}.AC_DEPRECATION.AC_DEPRECATION_ABOLISHED`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: `${this.translationsContainerKey}.AC_DEPRECATION.AC_DEPRECATION_ABOLISHED`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: `${this.translationsContainerKey}.AC_DEPRECATION.AC_DEPRECATION_ENABLED`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_EXCESS,
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_EXCESS}.tooltip`,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: isPevno ? 'yes' : `${this.translationsContainerKey}.AC_EXCESS.optional`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: isPevno ? 'yes' : `${this.translationsContainerKey}.AC_EXCESS.optional`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: isPevno ? 'yes' : `${this.translationsContainerKey}.AC_EXCESS.optional`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: isPevno ? 'yes' : `${this.translationsContainerKey}.AC_EXCESS.optional`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: isPevno ? 'yes' : `${this.translationsContainerKey}.AC_EXCESS.optional`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_VALUE_OF_VEHICLE,
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_VALUE_OF_VEHICLE}.tooltip`,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: vehicleValue
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: vehicleValue
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: vehicleValue
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: vehicleValue
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: vehicleValue
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_METHOD_VALUE_OF_VEHICLE,
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_VALUE_OF_VEHICLE}.tooltip`,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: `${this.translationsContainerKey}.AC_METHOD_VALUE_OF_VEHICLE.AC_METHOD_VALUE_OF_VEHICLE_VARIABLE`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: `${this.translationsContainerKey}.AC_METHOD_VALUE_OF_VEHICLE.AC_METHOD_VALUE_OF_VEHICLE_VARIABLE`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: `${this.translationsContainerKey}.AC_METHOD_VALUE_OF_VEHICLE.AC_METHOD_VALUE_OF_VEHICLE_VARIABLE`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: `${this.translationsContainerKey}.AC_METHOD_VALUE_OF_VEHICLE.AC_METHOD_VALUE_OF_VEHICLE_VARIABLE`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: `${this.translationsContainerKey}.AC_METHOD_VALUE_OF_VEHICLE.AC_METHOD_VALUE_OF_VEHICLE_VARIABLE`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_HAPPENING,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: true
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_HAPPENING_ELEMENTAL,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: true
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_HAPPENING_ANIMAL,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: true
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_VEHICLE_THEFT,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: true
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: true
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_VALUE_OF_DETRIMENT,
        values: [
          {
            coverage: AcVariantsModificationOption.SERWIS_ASO,
            value: `${this.translationsContainerKey}.AC_VALUE_OF_DETRIMENT.none`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT_PLUS,
            value: `${this.translationsContainerKey}.AC_VALUE_OF_DETRIMENT.none`
          },
          {
            coverage: AcVariantsModificationOption.WARSZTAT,
            value: `${this.translationsContainerKey}.AC_VALUE_OF_DETRIMENT.none`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI,
            value: `${this.translationsContainerKey}.AC_VALUE_OF_DETRIMENT.none`
          },
          {
            coverage: AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA,
            value: `${this.translationsContainerKey}.AC_VALUE_OF_DETRIMENT.none`
          }
        ]
      },
    ]
  }

  private getBeesafeFullComparisonConfig(): AcVariantsCoveragesCoverageConfigModel[] {
    return [
      {
        coverageName: AcVariantsCoverage.AC_VARIANT,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_VARIANT}.mini`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_VARIANT}.partnerWorkshop`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_VARIANT}.aso`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_VALUE_OF_VEHICLE,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: this.getDynamicCoverage('AC_MINI', OfferCoverageCode.AC_VALUE_OF_VEHICLE)
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: this.getDynamicCoverage('AC_WORKSHOP', OfferCoverageCode.AC_VALUE_OF_VEHICLE)
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: this.getDynamicCoverage('AC_SERVICE', OfferCoverageCode.AC_VALUE_OF_VEHICLE)
          }
        ],
        isAmount: true,
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_VALUE_OF_VEHICLE}.tooltip`
      },
      {
        coverageName: AcVariantsCoverage.AC_HAPPENING_ACCIDENT_AND_DAMAGE,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_HAPPENING_ACCIDENT_AND_DAMAGE}.ANIMAL_COLLISIONS`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: true
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_HAPPENING_ELEMENTAL,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: true
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_HAPPENING_ANIMAL,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: false
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: true
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_VEHICLE_THEFT,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: true
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: true
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_METHOD_VALUE_OF_VEHICLE,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_VALUE_OF_VEHICLE}.${this.getDynamicCoverage('AC_MINI', OfferCoverageCode.AC_METHOD_VALUE_OF_VEHICLE)}`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_VALUE_OF_VEHICLE}.${this.getDynamicCoverage('AC_WORKSHOP', OfferCoverageCode.AC_METHOD_VALUE_OF_VEHICLE)}`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_VALUE_OF_VEHICLE}.${this.getDynamicCoverage('AC_SERVICE', OfferCoverageCode.AC_METHOD_VALUE_OF_VEHICLE)}`
          }
        ],
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_VALUE_OF_VEHICLE}.tooltip`
      },
      {
        coverageName: AcVariantsCoverage.AC_DEPRECATION,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_DEPRECATION}.${this.getDynamicCoverage('AC_MINI', OfferCoverageCode.AC_DEPRECATION)}`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_DEPRECATION}.${this.getDynamicCoverage('AC_WORKSHOP', OfferCoverageCode.AC_DEPRECATION)}`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_DEPRECATION}.${this.getDynamicCoverage('AC_SERVICE', OfferCoverageCode.AC_DEPRECATION)}`
          }
        ],
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_DEPRECATION}.tooltip`
      },
      {
        coverageName: AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE}.estimate`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE}.partnerWorkshop`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE}.aso`
          }
        ],
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_METHOD_OF_LIQUIDATION_OF_THE_DAMAGE}.tooltip`
      },
      {
        coverageName: AcVariantsCoverage.AC_PARTS,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_PARTS}.replacements`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_PARTS}.replacements`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_PARTS}.original`
          }
        ]
      },
      {
        coverageName: AcVariantsCoverage.AC_EXCESS,
        values: [
          {
            coverage: BeesafeAcCoverageVariant.MINI,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_EXCESS}.500`
          },
          {
            coverage: BeesafeAcCoverageVariant.WORKSHOP,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_EXCESS}.500`
          },
          {
            coverage: BeesafeAcCoverageVariant.ASO,
            value: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_EXCESS}.500`
          }
        ],
        tooltip: `${this.translationsContainerKey}.${AcVariantsCoverage.AC_EXCESS}.tooltip`
      }
    ]
  }

  private getDynamicCoverage(optionName: string, coverageCode: OfferCoverageCode) {
    let offerModificationOption = this.selectedOfferService.selectedOffer
      .modifications.find(config => config.code === 'AC')
      .options.find(option => option.value === optionName);

    let coverage;
    if (isNotNullOrUndefined(offerModificationOption)) {
      coverage = offerModificationOption.coverages.find(cover => cover.code === coverageCode).value;
    }
    return coverage;
  }
}

export interface AcVariantsCoveragesCoverageConfigModel {
  coverageName: AcVariantsCoverage;
  values?: AcVariantCoverageValue[];
  tooltip?: string;
  isAmount?: boolean;
}

export interface AcVariantCoverageValue {
  coverage: AcVariantCoverage;
  value: string | boolean;
}

export interface AssistanceCoverageVariantPricing {
  name: AcVariantsCoveragesCoverageConfigModel;
  price: number;
}
